<template>
  <!-- A showcase of the different kinds of loaders, every single one inside a brand-card-lg with a title with the name -->
  <div>
    <el-collapse v-model="showcase" class="showcase-collapse m-6">
      <el-collapse-item title="Fonts" name="fonts">
        <!-- showing different tailwind font weights -->
        <div class="brand-card-lg m-6">
          <h2 class="font-thin">Thin</h2>
          <h2 class="font-light">Light</h2>
          <h2 class="font-normal">Normal</h2>
          <h2 class="font-medium">Medium</h2>
          <h2 class="font-semibold">Semi-Bold</h2>
          <h2 class="font-bold">Bold</h2>
          <h2 class="font-extrabold">Extra-Bold</h2>
          <h2 class="font-black">Black</h2>
        </div>
      </el-collapse-item>
      <el-collapse-item title="Editors" name="editors">
        <div>
          <!-- tailwindcss button style -->
          <button @click="copyToClipboard(exampleImage)" class="btn btn-blue">Copy example image</button>
          <button @click="copyToClipboard(exampleVideo)" class="btn btn-blue">Copy example video</button>
          <button class="btn btn-blue" @click="showSource = !showSource">{{ showSource ? 'Hide' : 'Show' }} Source</button>
        </div>

        <!-- mini editor-->
        <div class="row p-6 grid grid-cols-2 gap-4">
          <div>
            <MiniEditor :placeholder="miniPlaceholder" @update="updatedMini" @jsonUpdate="updatedMiniJson" />
            <div v-show="showSource">
              <div class="brand-card-lg mt-4 p-4">
                <h2 class="font-bold">Mini Editor Content:</h2>
                <pre>{{ miniEditorContent }}</pre>
              </div>
              <div class="brand-card-lg mt-4 p-4">
                <h2 class="font-bold">Mini Editor JSON:</h2>
                <pre>{{ miniEditorJson }}</pre>
              </div>
            </div>
          </div>
        </div>

        <!-- two columns tailwind -->
        <div class="row p-6 grid grid-cols-2 gap-4">
          <div>
            <Editor :placeholder="flowPlaceholder" @update="updatedEditor" @jsonUpdate="updatedJson" />
            <div v-show="showSource">
              <div class="brand-card-lg mt-4 p-4">
                <h2 class="font-bold">Editor Content:</h2>
                <pre>{{ editorContent }}</pre>
              </div>
              <div class="brand-card-lg mt-4 p-4">
                <h2 class="font-bold">Editor JSON:</h2>
                <pre>{{ editorJson }}</pre>
              </div>
            </div>
          </div>
          <div>
            <EmailContentEditor :placeholder="emailPlaceholder" @update="updatedEmail" @jsonUpdate="updatedEmailJson" />
            <div v-show="showSource">
              <div class="brand-card-lg mt-4 p-4">
                <h2 class="font-bold">Email Editor Content:</h2>
                <pre>{{ emailEditorContent }}</pre>
              </div>
              <div class="brand-card-lg mt-4 p-4">
                <h2 class="font-bold">Email Editor JSON:</h2>
                <pre>{{ emailEditorJson }}</pre>
              </div>
            </div>
          </div>
        </div>
      </el-collapse-item>
      <el-collapse-item title="Loaders" name="loaders">
        <div class="brand-card-lg m-6">
          <h2>Bar Loader</h2>
          <BarLoader />
        </div>
        <div class="brand-card-lg m-6">
          <h2>Beeswarm Loader</h2>
          <BeeswarmLoader />
        </div>
        <div class="brand-card-lg m-6">
          <h2>Campaign Editor Loader</h2>
          <CampaignEditorLoader />
        </div>
        <div class="brand-card-lg m-6">
          <h2>Dash Loader</h2>
          <DashLoader />
        </div>
        <div class="brand-card-lg m-6">
          <h2>Footer Loader</h2>
          <FooterLoader />
        </div>
        <div class="brand-card-lg m-6">
          <h2>Header Loader</h2>
          <HeaderLoader />
        </div>
        <div class="brand-card-lg m-6">
          <h2>Heatmap Loader</h2>
          <HeatmapLoader />
        </div>
        <div class="brand-card-lg m-6">
          <h2>Heatmap Section Loader</h2>
          <HeatmapSectionLoader />
        </div>
        <div class="brand-card-lg m-6">
          <h2>Horizontal Bar Loader</h2>
          <HorizontalBarLoader />
        </div>
        <div class="brand-card-lg m-6">
          <h2>Overall Loader</h2>
          <OverallLoader />
        </div>
        <div class="brand-card-lg m-6">
          <h2>Sankey Loader</h2>
          <SankeyLoader />
        </div>
        <div class="brand-card-lg m-6">
          <h2>Session List Loader</h2>
          <SessionListLoader />
        </div>
        <div class="brand-card-lg m-6">
          <h2>Small List Loader</h2>
          <SmallListLoader />
        </div>
        <div class="brand-card-lg m-6">
          <h2>Short List Loader</h2>
          <ShortListLoader />
        </div>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script>
/* Editor */
import Editor from '@/components/UI/Editor.vue';
import MiniEditor from '@/components/UI/MiniEditor.vue';
import EmailContentEditor from '@/components/UI/EmailContentEditor.vue';

/* Loaders */
import BarLoader from '@/components/UI/Loader/BarLoader.vue';
import BeeswarmLoader from '@/components/UI/Loader/BeeswarmLoader.vue';
import CampaignEditorLoader from '@/components/UI/Loader/CampaignEditorLoader.vue';
import DashLoader from '@/components/UI/Loader/DashLoader.vue';
import FooterLoader from '@/components/UI/Loader/FooterLoader.vue';
import HeaderLoader from '@/components/UI/Loader/HeaderLoader.vue';
import HeatmapLoader from '@/components/UI/Loader/HeatmapLoader.vue';
import HeatmapSectionLoader from '@/components/UI/Loader/HeatmapSectionLoader.vue';
import HorizontalBarLoader from '@/components/UI/Loader/HorizontalBarLoader.vue';
import OverallLoader from '@/components/UI/Loader/OverallLoader.vue';
import SankeyLoader from '@/components/UI/Loader/SankeyLoader.vue';
import SessionListLoader from '@/components/UI/Loader/SessionListLoader.vue';
import SmallListLoader from '@/components/UI/Loader/SmallListLoader.vue';
import ShortListLoader from '@/components/UI/Loader/ShortListLoader.vue';

/* Utils */
import { CopyToClipboard } from '@/mixins/copy-to-clipboard';

export default {
  name: 'Test',
  mixins: [CopyToClipboard],
  components: {
    Editor,
    MiniEditor,
    EmailContentEditor,
    BarLoader,
    BeeswarmLoader,
    CampaignEditorLoader,
    DashLoader,
    FooterLoader,
    HeaderLoader,
    HeatmapLoader,
    HeatmapSectionLoader,
    HorizontalBarLoader,
    OverallLoader,
    SankeyLoader,
    SessionListLoader,
    SmallListLoader,
    ShortListLoader,
  },
  data() {
    return {
      loading: true,
      showcase: ['editors'],
      editorContent: '',
      editorJson: '',
      emailEditorContent: '',
      emailEditorJson: '',
      miniEditorContent: '',
      miniEditorJson: '',
      showSource: false,
      exampleImage: 'https://www.adorama.com/alc/wp-content/uploads/2018/11/landscape-photography-tips-yosemite-valley-feature-825x465.jpg',
      exampleVideo: 'https://www.youtube.com/watch?v=ZxrBSad5FWQ',
      flowPlaceholder:
        '<p>Dear <merge-field id="CUSTOMER_NAME.FIRST" label="Customer First Name" fallback="customer" open="false">{{CUSTOMER_NAME.FIRST|customer}}</merge-field>:</p><p>Hi! You\'ve been with us for <merge-field id="SUBSCRIPTION_AGE" label="Subscription Age" fallback="a long time" open="false">{{SUBSCRIPTION_AGE|a long time}}</merge-field>.</p><p>This is a test.</p><hr>Testing plain merge fields: {{FULL_NAME|test}}',
      emailPlaceholder:
        '<p>Hello, <merge-field id="FIRST_NAME" label="First Name" fallback="dear customer" open="false">{{FIRST_NAME|dear customer}}</merge-field>!</p><p>This is a test!</p><p><cta text=""></cta></p>',
      miniPlaceholder: 'foo',
    };
  },
  methods: {
    updatedEditor(content) {
      this.editorContent = content;
    },
    updatedJson(json) {
      this.editorJson = json;
    },
    updatedEmail(content) {
      this.emailEditorContent = content;
    },
    updatedEmailJson(json) {
      this.emailEditorJson = json;
    },
    updatedMini(content) {
      this.miniEditorContent = content;
    },
    updatedMiniJson(json) {
      this.miniEditorJson = json;
    },
  },
};
</script>

<style lang="scss">
.showcase-collapse {
  .el-collapse-item__header {
    padding-left: 12px !important;
  }
  .editor, .mini-editor {
    border: 0px solid #B0B0B0;
    border-radius: 5px;
    box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.2);
  }
  pre {
    white-space: pre-wrap;
    word-wrap: break-word;
  }
}
.btn {
  @apply font-bold py-1 px-2 rounded ml-2 focus:outline-none;
}
.btn-blue {
  @apply bg-blue-500 text-white;
}
.btn-blue:hover {
  @apply bg-blue-700;
}
</style>
